<script lang="ts">
  import {onMount} from 'svelte';
  import {isMobile, subtitles} from '@/store';
  import type {Video} from '@/types/project';
  import Swal from 'sweetalert2';
  import {download, toTime} from '@/utils/common';
  import {SubtitlesService} from '../../../../services';

  export let selectedVideo: Video | null;
  interface AssemblyAIResponse {
    audio_duration: number;
    confidence: number;
    id: string;
    paragraphs: {
      text: string;
      start: number;
      end: number;
      confidence: number;
    }[];
  }
  const type = 'Transcription';
  let isLoadingTranscription = true;
  let transcription: AssemblyAIResponse | null = null;

  const handleDownload = async () => {
    const filename = `[${type}] ${selectedVideo?.title}.txt`;
    const text = transcription
      ? transcription.paragraphs.map(sentence => sentence.text).join('\n')
      : 'No transcription...';
    await download(filename, text, type);
  };

  onMount(async () => {
    if (!selectedVideo) throw 'selectedVideo is null';

    try {
      const text = await subtitles
        .downloadTranscript(selectedVideo.id)
        .then(subtitleURL => fetch(subtitleURL).then(response => response.text()));
      const formattedResponse = await new SubtitlesService().getTranscriptionByParagraphs(selectedVideo.id);
      if (typeof text !== 'string') throw 'subtitle text is not a string.';

      transcription = formattedResponse.data;
    } catch (error) {
      console.error(error);
      Swal.fire({
        position: 'bottom-end',
        icon: 'error',
        title: 'Transcription Failed to Download',
        showConfirmButton: false,
        timer: 10000,
        toast: true
      });
    }

    isLoadingTranscription = false;
  });
</script>

<div class="h-full w-full">
  <div class="flex h-16 w-full items-center border-b border-b-gray-200 px-4 dark:border-b-zinc-600">
    {#if !$isMobile}
      <h2 class="text-md w-full font-bold text-gray-900">{type}</h2>
    {/if}
    <div class="ml-auto flex w-full items-center lg:w-auto">
      <button
        on:click={() => handleDownload()}
        class="px-3 py-1 font-medium text-zinc-100 {isLoadingTranscription || transcription === null
          ? 'bg-zinc-400'
          : 'hoversermonbg bg-rose-500'} w-full rounded-md lg:w-auto"
        disabled={isLoadingTranscription || transcription === null}
      >
        Download
      </button>
    </div>
  </div>
  <div class="h-[calc(100%-4rem)] w-full overflow-auto p-4">
    {#if isLoadingTranscription}
      <div class="flex h-full w-full justify-center">
        <div class="flex flex-col items-center gap-4 pt-10">
          <div
            class="spinner h-8 w-8 !border-zinc-200 !border-t-rose-500 dark:!border-zinc-700 dark:!border-t-rose-500"
          ></div>
          <h1 class="text-3xl font-bold text-zinc-400 dark:text-zinc-400">Loading Transcriptions...</h1>
        </div>
      </div>
    {:else if transcription !== null}
      <div class="flex h-full w-full justify-center">
        <div class="h-full w-full max-w-[50rem]">
          <div class="w-full pb-10 text-justify">
            {#each transcription.paragraphs as sentence}
              <div class="mb-2 flex w-full items-baseline py-3 align-baseline">
                <span class="mr-3 block w-14 flex-shrink-0 select-none text-[0.7rem] text-slate-700"
                  >{toTime(sentence.start / 1000)}</span
                > <span class="block flex-grow text-xl leading-loose">{sentence.text}</span>
              </div>
            {/each}
            {#if transcription.audio_duration}
              <div class="w-full rounded-md bg-gray-100 p-4 text-right">
                <span class="font-bold text-slate-700">Total Duration {toTime(transcription.audio_duration)}</span>
              </div>
            {/if}
          </div>
        </div>
      </div>
    {:else}
      <div class="flex h-full w-full items-center justify-center">
        <div class="flex flex-col items-center justify-center gap-4">
          <h1 class="pt-10 text-3xl font-bold text-zinc-400 dark:text-zinc-400">No Transcription</h1>
        </div>
      </div>
    {/if}
  </div>
</div>
